import axios from 'axios';
import { auth } from './firebase';
import { logHttpError } from './utils/httpError';

// Base URL for API
// In production, the API is on the same domain, so we use a relative path.
// In development (when NODE_ENV is not 'production'), we use the full URL.
const BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:4000/api';

// Create axios instance with base URL
export const api = axios.create({
  baseURL: BASE_URL
});

// Create a separate instance for auth checks that swallows all errors
// This prevents errors from showing in the console
const authCheckApi = axios.create({
  baseURL: BASE_URL
});

// Track authentication errors
export const authErrors = {
  whitelistError: false,
  message: '',
  lastChecked: null,
};

// Authentication error handler callback
let authErrorHandlerCallback = null;

// Set a callback to handle auth errors
export const setAuthErrorHandler = (callback) => {
  authErrorHandlerCallback = callback;
};

// Reset auth errors
export const resetAuthErrors = () => {
  console.log("Resetting auth errors");
  authErrors.whitelistError = false;
  authErrors.message = '';
  authErrors.lastChecked = null;
};

// Function to manually check auth without throwing errors to the console
export const checkAuthAccess = async () => {
  try {
    // Only make the check if user is signed in
    const user = auth.currentUser;
    if (!user) {
      console.log("No current user for auth check");
      return { authorized: false };
    }
    
    console.log("Checking auth access for user:", user.email);
    
    try {
      // Get the token
      const token = await user.getIdToken();
      
      // Use a raw fetch instead of axios to avoid console errors
      const response = await fetch(`${BASE_URL}/summary`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log("Auth check response:", response.status);
      
      // Check the response
      if (response.ok) {
        // If we get here, user is authorized
        console.log("User is authorized");
        authErrors.whitelistError = false;
        authErrors.message = '';
        authErrors.lastChecked = new Date();
        return { authorized: true };
      } else if (response.status === 403) {
        // Try to parse the response body
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {
          console.log("Could not parse error response");
        }
        
        // Check if this is a whitelist error
        if (errorData && errorData.error === 'E‑mail not whitelisted') {
          console.log("User is not whitelisted:", user.email);
          authErrors.whitelistError = true;
          authErrors.message = 'Your email address is not authorized to access this application. Please contact the administrator.';
          authErrors.lastChecked = new Date();
          
          // Call the error handler if set
          if (authErrorHandlerCallback) {
            authErrorHandlerCallback('whitelist', authErrors.message);
          }
          
          return { 
            authorized: false, 
            whitelisted: false,
            message: authErrors.message
          };
        }
        
        // Some other 403 error
        return { 
          authorized: false, 
          whitelisted: undefined,
          statusCode: response.status
        };
      } else {
        // Other error status
        return { 
          authorized: false, 
          whitelisted: undefined,
          statusCode: response.status
        };
      }
    } catch (error) {
      console.log("Auth check network error:", error);
      
      return { 
        authorized: false, 
        whitelisted: undefined,
        error
      };
    }
  } catch (error) {
    console.error("Unexpected error in checkAuthAccess:", error);
    return { 
      authorized: false, 
      whitelisted: undefined,
      error
    };
  }
};

// Add interceptor to attach auth token to all requests
api.interceptors.request.use(async (config) => {
  // Get current user
  const user = auth.currentUser;
  
  // If user is logged in, get their token and add to request headers
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
  }
  
  // Add environment header
  try {
    config.headers = config.headers || {};
    config.headers['X-Bidsy-Env'] = localStorage.getItem('bidsy-env') || 'production';
  } catch (_) {}

  // Add a lightweight client trace id if not present
  try {
    const randomId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
    if (!config.headers['X-Client-TraceId']) {
      config.headers['X-Client-TraceId'] = randomId();
    }
  } catch (_) {}

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add the function to get funnel progression data
export const getFunnelProgressionData = async () => {
  try {
    const response = await api.get('/funnel-progression');
    return response.data;
  } catch (error) {
    console.error('Error fetching funnel progression data:', error);
    throw error;
  }
};

// Function to get the list of auction periods for the filter dropdown
export const getAuctionPeriods = async () => {
  try {
    const response = await api.get('/auction-periods');
    return response.data;
  } catch (error) {
    console.error('Error fetching auction periods:', error);
    throw error;
  }
};

// Function to get the detailed bids for a user's auctions
export const getBidsForUser = async (userId, periodId) => {
  try {
    const response = await api.get(`/users/${userId}/bids`, { params: { periodId } });
    return response.data;
  } catch (error) {
    console.error(`Error fetching bids for user ${userId}:`, error);
    throw error;
  }
};

// Function to get all bids placed by a specific user
export const getPlacedBidsForUser = async (userId, periodId) => {
  try {
    const response = await api.get(`/users/${userId}/placed-bids`, { params: { periodId } });
    return response.data;
  } catch (error) {
    console.error(`Error fetching placed bids for user ${userId}:`, error);
    throw error;
  }
};

// Function to get the unique users a user has bid on
export const getUniqueBidTargetsForUser = async (userId, periodId) => {
  try {
    const response = await api.get(`/users/${userId}/bid-targets`, { params: { periodId } });
    return response.data;
  } catch (error) {
    console.error(`Error fetching unique bid targets for user ${userId}:`, error);
    throw error;
  }
};

// ----- Daily Claims API helpers -----
export async function getDailyClaimsSeries(params = {}) {
  const { data } = await api.get('/claims/daily', { params });
  return data;
}
export async function getClaimsLeaderboard(params = {}) {
  const { data } = await api.get('/claims/leaderboard', { params });
  return data;
}
export async function getClaimsKpis(params = {}) {
  const { data } = await api.get('/claims/kpis', { params });
  return data;
}
export async function getUserClaims(userId) {
  const { data } = await api.get(`/users/${userId}/claims`);
  return data;
}

// ----- User Activity daily series helpers -----
export async function getSessionsDaily(params = {}) {
  const { data } = await api.get('/sessions/daily', { params });
  return data;
}

export async function getBidsDaily(params = {}) {
  const { data } = await api.get('/bids/daily', { params });
  return data;
}

// ----- SMS Reporting API helpers -----
export async function getSmsSummary(params = {}) {
  const { data } = await api.get('/sms/summary', { params });
  return data;
}
export async function getSmsDaily(params = {}) {
  const { data } = await api.get('/sms/daily', { params });
  return data;
}
export async function getSmsKeywords(params = {}) {
  const { data } = await api.get('/sms/keywords', { params });
  return data;
}
export async function getSmsConversations(params = {}) {
  const { data } = await api.get('/sms/conversations', { params });
  return data;
}
export async function getSmsCampaigns(params = {}) {
  const { data } = await api.get('/sms/campaigns', { params });
  return data;
}

// ----- Incoming SMS (admin) -----
export async function getIncomingSms(params = {}) {
  const { data } = await api.get('/sms/incoming', { params });
  return data;
}
export async function markIncomingSmsRead(id) {
  const { data } = await api.post(`/sms/incoming/${id}/mark-read`);
  return data;
}

// ----- Admin: Delete user -----
export async function adminDeleteUser(userId) {
  const { data } = await api.post(`/admin/delete-user/${userId}`);
  return data;
}

// ----- Admin: Delete user image -----
export async function adminDeleteImage(imageId, { permanentDelete = false } = {}) {
  const { data } = await api.post(`/admin/delete-image/${imageId}`, { permanent_delete: permanentDelete });
  return data;
}

// ----- Admin: Remove creator from browse -----
export async function adminRemoveCreatorFromBrowse(userId) {
  const { data } = await api.post(`/admin/remove-creator-from-browse/${userId}`);
  return data;
}

// ----- User Messages API helper -----
export async function getUserMessages(userId, params = {}) {
  const { data } = await api.get(`/users/${userId}/messages`, { params });
  return data;
}

export async function sendSmsToUser(userId, message) {
  const { data } = await api.post(`/users/${userId}/send-sms`, { message });
  return data; // { success, message, data }
}

// ----- Bulk SMS -----
export async function getBulkSmsUserPreview(userIds) {
  const { data } = await api.post('/sms/bulk-preview', { userIds });
  return data; // { success, users, notFoundIds, stats }
}

export async function sendBulkSms(userIds, message) {
  const { data } = await api.post('/sms/bulk-send', { userIds, message });
  return data; // { success, message, sentCount, data }
}

// ----- Redemptions API -----
export async function getRedemptions(params = {}) {
  const { data } = await api.get('/redemptions', { params });
  return data;
}
export async function markRedemptionPaid(id) {
  const { data } = await api.post(`/redemptions/${id}/mark-paid`);
  return data;
}
export async function markRedemptionFailed(id, reason = 'PayPal account not found or inactive') {
  const { data } = await api.post(`/redemptions/${id}/mark-failed`, { reason });
  return data;
}

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log all HTTP failures richly in the console (collapsed)
    try { logHttpError(error, 'API'); } catch {}
    
    // Check if we have a valid error response
    if (!error.response) {
      return Promise.reject(error);
    }
    
    // Specifically handle whitelist errors (403)
    if (error.response.status === 403) {
      if (error.response.data && error.response.data.error === 'E‑mail not whitelisted') {
        console.log("Setting whitelist error flag");
        authErrors.whitelistError = true;
        authErrors.message = 'Your email address is not authorized to access this application. Please contact the administrator.';
        authErrors.lastChecked = new Date();
        
        // Call the error handler if set
        if (authErrorHandlerCallback) {
          authErrorHandlerCallback('whitelist', authErrors.message);
        }
      }
    }
    
    // Handle 401 (Unauthorized) errors
    if (error.response.status === 401) {
      console.log("Unauthorized error");
      if (authErrorHandlerCallback) {
        authErrorHandlerCallback('unauthorized', 'Authentication required');
      }
    }
    
    return Promise.reject(error);
  }
); 

// ----- Connections reporting -----
export async function getConnectionsSummary(params = {}) {
  const { data } = await api.get('/connections/summary', { params });
  return data;
}
export async function getConnectionsDaily(params = {}) {
  const { data } = await api.get('/connections/daily', { params });
  return data;
}
export async function getTopAccepted(params = {}) {
  const { data } = await api.get('/connections/top-accepted', { params });
  return data;
}
export async function getRewarded(params = {}) {
  const { data } = await api.get('/connections/rewarded', { params });
  return data;
}
export async function getAcceptedPartners(userId, params = {}) {
  const { data } = await api.get('/connections/accepted-partners', { params: { userId, ...params } });
  return data;
}

// ----- Right now: open connections and unpaid cash-out requests -----
// The same read the admin alerts use, so a tile and a text cannot disagree.
export async function getLiveActivity() {
  const { data } = await api.get('/live-activity');
  return data;
}

// ----- Video bookings and their clocks (admin panel plan, routes 24 to 26) -----
export async function getVideoBooking(connectionId) {
  const { data } = await api.get(`/video-bookings/${connectionId}`);
  return data;
}

export async function retryVideoClock(clockId) {
  const { data } = await api.post(`/video-clocks/${clockId}/retry`);
  return data;
}

export async function getVideoCases(params = {}) {
  const { data } = await api.get('/video-cases', { params });
  return data;
}

export async function getVideoCase(caseId) {
  const { data } = await api.get(`/video-cases/${caseId}`);
  return data;
}

export async function settleVideoCase(caseId, body) {
  const { data } = await api.post(`/video-cases/${caseId}/settle`, body);
  return data;
}

export async function viewVideoRecording(recordingId, reason) {
  const { data } = await api.post(`/video-recordings/${recordingId}/view`, reason ? { reason } : undefined);
  return data;
}

export async function removeVideoRecording(recordingId, body) {
  const { data } = await api.post(`/video-recordings/${recordingId}/remove`, body);
  return data;
}

export async function getVideoOps() {
  const { data } = await api.get('/video-ops');
  return data;
}

export async function getVideoOpsAudit() {
  const { data } = await api.get('/video-ops/audit');
  return data;
}

// ----- Per-user connections -----
export async function getUserConnections(userId, params = {}) {
  const { data } = await api.get(`/users/${userId}/connections`, { params });
  return data;
}

// ----- Per-user ratings -----
export async function getUserRatings(userId) {
  const { data } = await api.get(`/users/${userId}/ratings`);
  return data;
}

// ----- Per-user auctions -----
export async function getUserAuctions(userId) {
  const { data } = await api.get(`/users/${userId}/auctions`);
  return data;
}

// ----- Chat (Firebase RTDB proxy) -----
// Removed: chat conversation proxy; client reads from Firebase RTDB directly
// ----- Campaigns Reporting API helpers -----
export async function getCampaignOverview(params = {}) {
  const { data } = await api.get('/campaigns/overview', { params });
  return data;
}
export async function getCampaignFunnelByCampaign(campaignId) {
  const { data } = await api.get('/campaigns/funnel-by-campaign', { params: { campaignId } });
  return data;
}
export async function getCampaignFunnelByCategory(categoryId) {
  const { data } = await api.get('/campaigns/funnel-by-category', { params: { categoryId } });
  return data;
}

// ----- Campaign Acquisition API helpers -----
export async function getCampaignAcquisitionByCampaign(campaignId, params = {}) {
  const { data } = await api.get('/campaigns/acquisition-by-campaign', { params: { campaignId, ...params } });
  return data;
}
export async function getCampaignAcquisitionByCategory(categoryId, params = {}) {
  const { data } = await api.get('/campaigns/acquisition-by-category', { params: { categoryId, ...params } });
  return data;
}

// Returns true if at least one unapproved user exists.
export async function hasPendingUserApprovals() {
  try {
    const { data } = await api.get('/admin/next-unapproved-image');
    // Endpoint returns either a record (object) or null
    return !!data;
  } catch (e) {
    // Treat errors as "unknown/none" so we don't spam the UI
    return false;
  }
}

// Lightweight event broadcaster so other components (e.g., Navigation) can react instantly
export function notifyUserApprovalPending(hasAny) {
  try {
    window.dispatchEvent(
      new CustomEvent('user-approvals:pending', { detail: { hasAny: !!hasAny } })
    );
  } catch (_) {}
}

// Returns true if at least one pending user image exists.
export async function hasPendingProfileImageApprovals() {
  try {
    const { data } = await api.get('/admin/pending-user-images');
    return data?.data?.length > 0;
  } catch (e) {
    return false;
  }
}

// Lightweight event broadcaster so other components (e.g., Navigation) can react instantly
export function notifyProfileImageApprovalPending(hasAny) {
  try {
    window.dispatchEvent(
      new CustomEvent('profile-image-approvals:pending', { detail: { hasAny: !!hasAny } })
    );
  } catch (_) {}
}

// Returns true if at least one pending social handle exists.
export async function hasPendingSocialHandleApprovals() {
  try {
    const { data } = await api.get('/admin/pending-social-media-handles');
    return data?.data?.length > 0;
  } catch (e) {
    return false;
  }
}

// Returns true if at least one pending creator exists.
export async function hasPendingCreatorApprovals() {
  try {
    const { data } = await api.get('/admin/pending-creators');
    return data?.data?.length > 0;
  } catch (e) {
    return false;
  }
}

// Lightweight event broadcaster for creator approvals
export function notifyCreatorApprovalPending(hasAny) {
  try {
    window.dispatchEvent(
      new CustomEvent('creator-approvals:pending', { detail: { hasAny: !!hasAny } })
    );
  } catch (_) {}
}

// Lightweight event broadcaster for social handle approvals
export function notifySocialHandleApprovalPending(hasAny) {
  try {
    window.dispatchEvent(
      new CustomEvent('social-handle-approvals:pending', { detail: { hasAny: !!hasAny } })
    );
  } catch (_) {}
}

// Returns true if at least one redemption is pending
export async function hasPendingRedemptions() {
  try {
    const { data } = await api.get('/redemptions', { params: { status: 'pending', limit: 1, offset: 0 } });
    // Endpoint returns { data: [...], meta: { total, ... } }
    const total = Number(data?.meta?.total ?? 0);
    const rows  = Array.isArray(data?.data) ? data.data.length : 0;
    return total > 0 || rows > 0;
  } catch {
    // Fail-soft: assume none to avoid noisy UI
    return false;
  }
}

// Lightweight event broadcaster so Navigation can react instantly
export function notifyRedemptionsPending(hasAny) {
  try {
    window.dispatchEvent(
      new CustomEvent('redemptions:pending', { detail: { hasAny: !!hasAny } })
    );
  } catch (_) {}
}

// Returns true if there are unread incoming SMS messages
export async function hasUnreadIncomingSms() {
  try {
    const { data } = await api.get('/sms/has-unread');
    return !!data;
  } catch {
    // Fail-soft: assume none to avoid noisy UI
    return false;
  }
}

// Lightweight event broadcaster so Navigation can react instantly
export function notifyIncomingSmsUnread(hasAny) {
  try {
    window.dispatchEvent(
      new CustomEvent('incoming-sms:unread', { detail: { hasAny: !!hasAny } })
    );
  } catch (_) {}
}

// ----- Creator Funnel API helper -----
export async function getCreatorFunnel() {
  const { data } = await api.get('/creator-funnel');
  return data;
}

// ----- Period Reporting API helper -----
export async function getPeriodReporting() {
  const { data } = await api.get('/period-reporting');
  return data; // { rows: [...] }
}

// ----- Secure Video URL helper -----
export async function getSecureVideoUrl(userId) {
  try {
    const { data } = await api.get(`/secure-video-url/${userId}`);
    return data; // { url }
  } catch (error) {
    // Return null if no video found (404) or other error
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// ----- Secure Deleted Image URL helper -----
export async function getSecureDeletedImageUrl(userId) {
  try {
    const { data } = await api.get(`/secure-deleted-image-url/${userId}`);
    return data; // { urls: [...] }
  } catch (error) {
    // Return null if no deleted images found (404) or other error
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// ----- Admin Notification Settings -----
export async function getAdminNotificationSettings() {
  const { data } = await api.get('/admin/notification-settings');
  return data;
}

export async function updateAdminNotificationSettings(userId, notifications) {
  const { data } = await api.post(`/admin/notification-settings/${userId}`, { notifications });
  return data;
}

export async function deleteAdminNotificationSettings(userId) {
  const { data } = await api.delete(`/admin/notification-settings/${userId}`);
  return data;
}

// Live health of every pending-item check, so a failing check is visible in the
// dashboard rather than only in the server log.
export async function getNotificationStatus() {
  const { data } = await api.get('/admin/notification-status');
  return data;
}

// Sends a real SMS through the notification path to confirm delivery works.
export async function sendTestNotification(userId) {
  const { data } = await api.post(`/admin/notification-test/${userId}`);
  return data;
}

// ----- Rate Limit Logs -----
export async function getRateLimitLogs(params = {}) {
  const { data } = await api.get('/rate-limit-logs', { params });
  return data;
}

// ----- Notification Content (Bidsy notification templates) -----
export async function getNotificationContent() {
  const { data } = await api.get('/notification-content');
  return data;
}

export async function saveNotificationTemplate(key, payload) {
  const { data } = await api.put(`/notification-content/${encodeURIComponent(key)}`, payload);
  return data;
}

export async function resetNotificationTemplate(key, payload = {}) {
  const { data } = await api.delete(`/notification-content/${encodeURIComponent(key)}`, { data: payload });
  return data;
}

export async function getNotificationTemplateDetail(key) {
  const { data } = await api.get(`/notification-content/${encodeURIComponent(key)}`);
  return data;
}

export async function previewNotificationTemplate(key, payload = {}) {
  const { data } = await api.post(`/notification-content/${encodeURIComponent(key)}/preview`, payload);
  return data;
}

export async function previewNotificationPublish({ signal } = {}) {
  const { data } = await api.post('/notification-publish/preview', {}, { signal });
  return data;
}

export async function publishNotifications(payload, { signal } = {}) {
  const { data } = await api.post('/notification-publish', payload, { signal });
  return data;
}

export async function previewNotificationSyncToDev({ signal } = {}) {
  const { data } = await api.post('/notification-sync-to-dev/preview', {}, { signal });
  return data;
}

export async function syncNotificationsToDev(payload, { signal } = {}) {
  const { data } = await api.post('/notification-sync-to-dev', payload, { signal });
  return data;
}

// ----- Notification Triggers (Bidsy trigger matrix + per-channel settings) -----
export async function getNotificationTriggers() {
  const { data } = await api.get('/notification-triggers');
  return data;
}

export async function setTriggerChannel(trigger, channel, payload) {
  const { data } = await api.put(
    `/notification-triggers/${encodeURIComponent(trigger)}/channels/${encodeURIComponent(channel)}`,
    payload
  );
  return data;
}

export async function resetTriggerChannel(trigger, channel) {
  const { data } = await api.delete(
    `/notification-triggers/${encodeURIComponent(trigger)}/channels/${encodeURIComponent(channel)}`
  );
  return data;
}

export async function getTriggerRevisions(trigger, params = {}) {
  const { data } = await api.get(`/notification-triggers/${encodeURIComponent(trigger)}/revisions`, { params });
  return data;
}

// ----- Execute Custom Query -----
export async function executeQuery(query) {
  const { data } = await api.post('/execute-query', { query });
  return data;
}
